import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MinusIcon, PlusIcon, ShoppingCartIcon, ZapIcon, TruckIcon, ShieldCheckIcon, RotateCcwIcon, HeartIcon, ShareIcon, CheckIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge, getBadgeVariant } from '../components/ui/Badge';
import { Rating } from '../components/ui/Rating';
import { Tabs } from '../components/ui/Tabs';
import { ProductCard } from '../components/product/ProductCard';
import { ProductGallery } from '../components/product/ProductGallery';
import { useCart } from '../context/CartContext';
import { getProductById, getRelatedProducts, getCrossSellProducts, products } from '../data/products';
import { getCategoryBySlug } from '../data/categories';
export function ProductPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    addToCart
  } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const product = getProductById(id || '');
  if (!product) {
    return <main className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-1033">
        <div className="text-center" data-id="element-1034">
          <h1 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1035">
            Товар не знайдено
          </h1>
          <Link to="/catalog" className="text-sky-600 hover:text-sky-700" data-id="element-1036">
            Повернутися до каталогу
          </Link>
        </div>
      </main>;
  }
  const category = getCategoryBySlug(product.categorySlug);
  const relatedProducts = getRelatedProducts(product, 4);
  const crossSellProducts = product.crossSell ? getCrossSellProducts(product.crossSell) : [];
  const images = product.images || [product.image];
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('uk-UA').format(price);
  };
  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };
  const handleBuyNow = () => {
    addToCart(product, quantity);
    navigate('/cart');
  };
  const tabs = [{
    id: 'description',
    label: 'Опис',
    content: <div className="prose prose-slate max-w-none" data-id="element-1037">
          <p className="text-slate-600 leading-relaxed" data-id="element-1038">
            {product.description || product.shortDesc || 'Опис товару відсутній.'}
          </p>
        </div>
  }, {
    id: 'characteristics',
    label: 'Характеристики',
    content: <div className="space-y-3" data-id="element-1039">
          {product.characteristics ? product.characteristics.map((char, index) => <div key={index} className={`flex justify-between py-3 ${index !== product.characteristics!.length - 1 ? 'border-b border-slate-100' : ''}`} data-id="element-1040">
                <span className="text-slate-600" data-id="element-1041">{char.label}</span>
                <span className="font-medium text-slate-900" data-id="element-1042">{char.value}</span>
              </div>) : <p className="text-slate-500" data-id="element-1043">Характеристики не вказані</p>}
        </div>
  }, {
    id: 'reviews',
    label: `Відгуки (${product.reviewCount})`,
    content: <div className="text-center py-8" data-id="element-1044">
          <p className="text-slate-500 mb-4" data-id="element-1045">
            Відгуки про цей товар поки відсутні
          </p>
          <Button variant="outline" data-id="element-1046">Написати відгук</Button>
        </div>
  }, {
    id: 'questions',
    label: 'Питання',
    content: <div className="text-center py-8" data-id="element-1047">
          <p className="text-slate-500 mb-4" data-id="element-1048">Маєте питання про товар?</p>
          <a href="https://t.me/aquapools_if" target="_blank" rel="noopener noreferrer" data-id="element-1049">
            <Button data-id="element-1050">Запитати в Telegram</Button>
          </a>
        </div>
  }];
  return <main className="min-h-screen bg-slate-50" data-id="element-1051">
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-slate-100" data-id="element-1052">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-1053">
          <nav className="flex items-center gap-2 text-sm flex-wrap" data-id="element-1054">
            <Link to="/" className="text-slate-500 hover:text-slate-700" data-id="element-1055">
              Головна
            </Link>
            <span className="text-slate-300" data-id="element-1056">/</span>
            <Link to="/catalog" className="text-slate-500 hover:text-slate-700" data-id="element-1057">
              Каталог
            </Link>
            {category && <>
                <span className="text-slate-300" data-id="element-1058">/</span>
                <Link to={`/category/${category.slug}`} className="text-slate-500 hover:text-slate-700" data-id="element-1059">
                  {category.name}
                </Link>
              </>}
            <span className="text-slate-300" data-id="element-1060">/</span>
            <span className="text-slate-900 font-medium truncate max-w-[200px]" data-id="element-1061">
              {product.name}
            </span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1062">
        {/* Product Main Section */}
        <div className="bg-white rounded-2xl shadow-md p-6 lg:p-8 mb-8" data-id="element-1063">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12" data-id="element-1064">
            {/* Gallery */}
            <ProductGallery images={images} productName={product.name} data-id="element-1065" />

            {/* Product Info */}
            <div data-id="element-1066">
              {/* Badge */}
              {product.badge && <div className="mb-4" data-id="element-1067">
                  <Badge variant={getBadgeVariant(product.badge)} data-id="element-1068">
                    {product.badge}
                  </Badge>
                </div>}

              {/* Name */}
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4" data-id="element-1069">
                {product.name}
              </h1>

              {/* Rating */}
              <div className="flex items-center gap-4 mb-6" data-id="element-1070">
                <Rating value={product.rating} reviewCount={product.reviewCount} size="md" data-id="element-1071" />
                <span className="text-sm text-slate-500" data-id="element-1072">
                  Код: {product.id}
                </span>
              </div>

              {/* Short Description */}
              {product.shortDesc && <p className="text-slate-600 mb-6" data-id="element-1073">{product.shortDesc}</p>}

              {/* Price */}
              <div className="mb-6" data-id="element-1074">
                <div className="flex items-baseline gap-3" data-id="element-1075">
                  <span className="text-3xl font-bold text-slate-900" data-id="element-1076">
                    {formatPrice(product.price)} ₴
                  </span>
                  {product.oldPrice && <span className="text-xl text-slate-400 line-through" data-id="element-1077">
                      {formatPrice(product.oldPrice)} ₴
                    </span>}
                </div>
                {product.oldPrice && <span className="text-sm text-red-500 font-medium" data-id="element-1078">
                    Економія: {formatPrice(product.oldPrice - product.price)} ₴
                  </span>}
              </div>

              {/* Availability */}
              <div className="flex items-center gap-2 mb-6" data-id="element-1079">
                {product.inStock ? <>
                    <CheckIcon className="w-5 h-5 text-emerald-500" data-id="element-1080" />
                    <span className="text-emerald-600 font-medium" data-id="element-1081">
                      В наявності
                    </span>
                  </> : <span className="text-slate-500" data-id="element-1082">Немає в наявності</span>}
              </div>

              {/* Quantity & Add to Cart */}
              {product.inStock && <div className="space-y-4 mb-8" data-id="element-1083">
                  <div className="flex items-center gap-4" data-id="element-1084">
                    <span className="text-sm text-slate-600" data-id="element-1085">Кількість:</span>
                    <div className="flex items-center gap-2 bg-slate-100 rounded-xl p-1" data-id="element-1086">
                      <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2 hover:bg-white rounded-lg transition-colors" disabled={quantity <= 1} data-id="element-1087">
                        <MinusIcon className="w-4 h-4 text-slate-600" data-id="element-1088" />
                      </button>
                      <span className="w-12 text-center font-medium" data-id="element-1089">
                        {quantity}
                      </span>
                      <button onClick={() => setQuantity(quantity + 1)} className="p-2 hover:bg-white rounded-lg transition-colors" data-id="element-1090">
                        <PlusIcon className="w-4 h-4 text-slate-600" data-id="element-1091" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3" data-id="element-1092">
                    <Button size="lg" onClick={handleAddToCart} leftIcon={addedToCart ? <CheckIcon className="w-5 h-5" data-id="element-1094" /> : <ShoppingCartIcon className="w-5 h-5" data-id="element-1095" />} className={`flex-1 ${addedToCart ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`} data-id="element-1093">
                      {addedToCart ? 'Додано!' : 'Додати в кошик'}
                    </Button>
                    <Button size="lg" variant="secondary" onClick={handleBuyNow} leftIcon={<ZapIcon className="w-5 h-5" data-id="element-1097" />} className="flex-1" data-id="element-1096">
                      Купити в 1 клік
                    </Button>
                  </div>
                </div>}

              {/* Quick Actions */}
              <div className="flex items-center gap-4 mb-8" data-id="element-1098">
                <button className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors" data-id="element-1099">
                  <HeartIcon className="w-5 h-5" data-id="element-1100" />В обране
                </button>
                <button className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors" data-id="element-1101">
                  <ShareIcon className="w-5 h-5" data-id="element-1102" />
                  Поділитися
                </button>
              </div>

              {/* Info Blocks */}
              <div className="grid grid-cols-2 gap-4" data-id="element-1103">
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl" data-id="element-1104">
                  <TruckIcon className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" data-id="element-1105" />
                  <div data-id="element-1106">
                    <p className="text-sm font-medium text-slate-900" data-id="element-1107">
                      Доставка
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-1108">Нова Пошта, кур'єр</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl" data-id="element-1109">
                  <ShieldCheckIcon className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" data-id="element-1110" />
                  <div data-id="element-1111">
                    <p className="text-sm font-medium text-slate-900" data-id="element-1112">
                      Гарантія
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-1113">Офіційна гарантія</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl" data-id="element-1114">
                  <RotateCcwIcon className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" data-id="element-1115" />
                  <div data-id="element-1116">
                    <p className="text-sm font-medium text-slate-900" data-id="element-1117">
                      Повернення
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-1118">14 днів</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl" data-id="element-1119">
                  <ShoppingCartIcon className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" data-id="element-1120" />
                  <div data-id="element-1121">
                    <p className="text-sm font-medium text-slate-900" data-id="element-1122">Оплата</p>
                    <p className="text-xs text-slate-500" data-id="element-1123">
                      Карткою, післяплата
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-md p-6 lg:p-8 mb-8" data-id="element-1124">
          <Tabs tabs={tabs} data-id="element-1125" />
        </div>

        {/* Cross-sell */}
        {crossSellProducts.length > 0 && <div className="mb-8" data-id="element-1126">
            <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-1127">
              Разом купують
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-id="element-1128">
              {crossSellProducts.map(p => <ProductCard key={p.id} product={p} data-id="element-1129" />)}
            </div>
          </div>}

        {/* Related Products */}
        {relatedProducts.length > 0 && <div data-id="element-1130">
            <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-1131">
              Схожі товари
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-id="element-1132">
              {relatedProducts.map(p => <ProductCard key={p.id} product={p} data-id="element-1133" />)}
            </div>
          </div>}
      </div>
    </main>;
}